import React from 'react';
import { Separator } from '../Separator';
import { hourText, type LocalHourInsight as Insight } from '../../utils/localHours';
interface LocalHourInsightProps {
  insight: Insight;
}

/**
 * Turns the distribution into plain language: how to read the chart, then what
 * this particular window actually did to people. Every figure is derived from the
 * measured data — nothing is asserted that the chart doesn't show.
 */
export function LocalHourInsight({
  insight
}: LocalHourInsightProps) {
  const overnight = Math.round(insight.overnightShare * 100);
  const workday = Math.round(insight.workdayShare * 100);
  const hasQuiet = insight.quietFrom !== null && insight.quietTo !== null;
  return <div className="mt-5" data-id="element-181" data-mp="h720uvt0d5di">
      <Separator data-id="element-182" data-mp="h57hvn1arnc2" />
      <dl className="mt-4 space-y-3 text-xs leading-relaxed text-muted-foreground" data-id="element-183" data-mp="h5qwatrqntlc">
        <div data-id="element-184" data-mp="m76joshe2c6w">
          <dt className="font-medium text-foreground" data-id="element-185" data-mp="m7g8wdum0fbj">How to read it</dt>
          <dd className="mt-1" data-id="element-186" data-mp="q807llutzqzs">
            Each bar is one hour on the visitor&rsquo;s own clock, so 9am means 9am wherever they
            are. It is a hour-of-day profile, not a timeline — bars don&rsquo;t run in chronological
            order, because 11pm for one person and 3am for another can be the same real minute. For
            when load arrived, read the traffic pattern chart above instead.
          </dd>
        </div>

        <div data-id="element-187" data-mp="a445wr02hkzl">
          <dt className="font-medium text-foreground" data-id="element-188" data-mp="17pi97fg6lg17">What this window did</dt>
          <dd className="mt-1" data-id="element-189" data-mp="17rkwvstv2x2r">
            Demand peaked at {hourText(insight.peakHour)} local ({insight.peakRequests.toLocaleString()}{' '}
            requests). {overnight}% of all requests came from people between midnight and 6am their
            time
            {overnight >= 25 ? ' — a large share were up in the middle of their night, so this window landed badly for at least one major region.' : ' — most people were awake when they hit the page.'}{' '}
            {workday}% arrived during normal working hours (9am–6pm local).
          </dd>
        </div>

        {hasQuiet && <div data-id="element-190" data-mp="3kvyfu2k4796f">
            <dt className="font-medium text-foreground" data-id="element-191" data-mp="3kwq2gnvqhwkk">
              Why {hourText(insight.quietFrom as number)}–{hourText(insight.quietTo as number)} is
              empty
            </dt>
            <dd className="mt-1" data-id="element-192" data-mp="3b5vbj67jm8ss">
              A four-hour window only covers four hours of clock time, so each region&rsquo;s
              visitors land in a narrow band. An empty stretch means no visitors were in the time
              zones where the window fell during those hours — not that traffic dropped.
            </dd>
          </div>}

        <div data-id="element-193" data-mp="2naka77gj321n">
          <dt className="font-medium text-foreground" data-id="element-194" data-mp="2nasnv1kh6bdw">What to do with it</dt>
          <dd className="mt-1" data-id="element-195" data-mp="2s7ssr95l983f">
            To make the next window hurt less, choose UTC hours that map to the small hours in your{' '}
            <em data-id="element-196" data-mp="qfxjt1apvhow">smallest</em> user regions rather than your largest. Compare against the Countries
            card to see which regions those overnight bars belong to.
          </dd>
        </div>
      </dl>
    </div>;
}